import { useMemo, useState } from "react";
import { Button, FormField, Input, Select, Textarea, Badge, Card } from "@/ui";
import { useT } from "@/app";
import { token } from "@/design/theme";
import type { IdentiteInput, RegisterDossierBody, Sexe } from "../lib/types";

/** The D-13 mandatory identity keys — required non-empty before submit is enabled. */
const REQUIRED: ReadonlyArray<keyof IdentiteInput> = [
  "nom",
  "prenom",
  "sexe",
  "date_naissance",
  "telephone",
  "nin",
  "wilaya",
  "commune",
  "adresse",
];

const EMPTY: IdentiteInput = {
  nom: "",
  prenom: "",
  sexe: "i",
  date_naissance: "",
  telephone: "",
  nin: "",
  wilaya: "",
  commune: "",
  adresse: "",
};

export interface IdentiteFormProps {
  saving: boolean;
  onSubmit: (body: RegisterDossierBody) => void;
  onCancel?: () => void;
  /** Called as nom/telephone change so the parent can run live intra-lab dedup. */
  onDedupSignalChange?: (signal: { nom: string; telephone: string }) => void;
  /** Called when NIN field loses focus — parent runs the cross-lab suggest (R-11). */
  onNinBlur?: (nin: string) => void;
  /** Slot for the dedup panel the parent renders below the name/phone fields. */
  dedupSlot?: React.ReactNode;
}

/**
 * IdentiteForm — keyboard-first dossier+patient intake. Mandatory identity (D-13) is required and
 * validated client-side before submit (server re-validates). Emits dedup signals so the parent shows
 * the live "dossier existant ?" panel.
 */
export function IdentiteForm({
  saving,
  onSubmit,
  onCancel,
  onDedupSignalChange,
  onNinBlur,
  dedupSlot,
}: IdentiteFormProps) {
  const { t } = useT();
  const [identite, setIdentite] = useState<IdentiteInput>(EMPTY);
  const [clinique, setClinique] = useState({
    groupe_sanguin: "",
    antecedents: "",
    traitements: "",
    allergies: "",
  });
  const [touched, setTouched] = useState<Partial<Record<keyof IdentiteInput, boolean>>>({});

  const set = (key: keyof IdentiteInput, value: string) => {
    const next = { ...identite, [key]: value };
    setIdentite(next);
    if ((key === "nom" || key === "telephone") && onDedupSignalChange) {
      onDedupSignalChange({ nom: next.nom, telephone: next.telephone });
    }
  };

  const missing = useMemo(
    () => REQUIRED.filter((k) => identite[k].trim() === ""),
    [identite],
  );
  const valid = missing.length === 0;

  const errFor = (k: keyof IdentiteInput) =>
    touched[k] && identite[k].trim() === "" ? t("reception.form.required") : undefined;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) {
      const all: Partial<Record<keyof IdentiteInput, boolean>> = {};
      for (const k of REQUIRED) all[k] = true;
      setTouched(all);
      return;
    }
    const dossier: NonNullable<RegisterDossierBody["dossier"]> = {};
    if (clinique.groupe_sanguin.trim()) dossier.groupe_sanguin = clinique.groupe_sanguin.trim();
    if (clinique.antecedents.trim()) dossier.antecedents = clinique.antecedents.trim();
    if (clinique.traitements.trim()) dossier.traitements = clinique.traitements.trim();
    if (clinique.allergies.trim()) dossier.allergies = clinique.allergies.trim();
    const body: RegisterDossierBody = { identite };
    if (Object.keys(dossier).length > 0) body.dossier = dossier;
    onSubmit(body);
  };

  const sexeOptions: Array<{ value: Sexe; label: string }> = [
    { value: "m", label: t("reception.field.sexe.m") },
    { value: "f", label: t("reception.field.sexe.f") },
    { value: "i", label: t("reception.field.sexe.i") },
  ];

  return (
    <form onSubmit={submit} noValidate style={{ display: "flex", flexDirection: "column", gap: token.s5 }}>
      <Card title={t("reception.section.identite")}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: token.s4,
          }}
        >
          <FormField label={t("reception.field.nom")} required error={errFor("nom")}>
            {(p) => (
              <Input
                {...p}
                autoFocus
                value={identite.nom}
                onChange={(e) => set("nom", e.target.value)}
                onBlur={() => setTouched((s) => ({ ...s, nom: true }))}
              />
            )}
          </FormField>
          <FormField label={t("reception.field.prenom")} required error={errFor("prenom")}>
            {(p) => (
              <Input
                {...p}
                value={identite.prenom}
                onChange={(e) => set("prenom", e.target.value)}
                onBlur={() => setTouched((s) => ({ ...s, prenom: true }))}
              />
            )}
          </FormField>
          <FormField label={t("reception.field.sexe")} required>
            {(p) => (
              <Select
                {...p}
                value={identite.sexe}
                onChange={(e) => set("sexe", e.target.value)}
                options={sexeOptions}
              />
            )}
          </FormField>
          <FormField label={t("reception.field.dateNaissance")} required error={errFor("date_naissance")}>
            {(p) => (
              <Input
                {...p}
                type="date"
                value={identite.date_naissance}
                onChange={(e) => set("date_naissance", e.target.value)}
                onBlur={() => setTouched((s) => ({ ...s, date_naissance: true }))}
              />
            )}
          </FormField>
          <FormField label={t("reception.field.telephone")} required error={errFor("telephone")}>
            {(p) => (
              <Input
                {...p}
                mono
                inputMode="tel"
                value={identite.telephone}
                onChange={(e) => set("telephone", e.target.value)}
                onBlur={() => setTouched((s) => ({ ...s, telephone: true }))}
              />
            )}
          </FormField>
          <FormField
            label={t("reception.field.nin")}
            required
            error={errFor("nin")}
            hint={<Badge tone="info">NIN</Badge>}
          >
            {(p) => (
              <Input
                {...p}
                mono
                value={identite.nin}
                onChange={(e) => set("nin", e.target.value)}
                onBlur={() => {
                  setTouched((s) => ({ ...s, nin: true }));
                  if (onNinBlur && identite.nin.trim()) onNinBlur(identite.nin.trim());
                }}
              />
            )}
          </FormField>
          <FormField label={t("reception.field.wilaya")} required error={errFor("wilaya")}>
            {(p) => (
              <Input
                {...p}
                value={identite.wilaya}
                onChange={(e) => set("wilaya", e.target.value)}
                onBlur={() => setTouched((s) => ({ ...s, wilaya: true }))}
              />
            )}
          </FormField>
          <FormField label={t("reception.field.commune")} required error={errFor("commune")}>
            {(p) => (
              <Input
                {...p}
                value={identite.commune}
                onChange={(e) => set("commune", e.target.value)}
                onBlur={() => setTouched((s) => ({ ...s, commune: true }))}
              />
            )}
          </FormField>
          <FormField label={t("reception.field.adresse")} required error={errFor("adresse")}>
            {(p) => (
              <Input
                {...p}
                value={identite.adresse}
                onChange={(e) => set("adresse", e.target.value)}
                onBlur={() => setTouched((s) => ({ ...s, adresse: true }))}
              />
            )}
          </FormField>
        </div>
        {dedupSlot}
      </Card>

      <Card title={t("reception.section.dossier")}>
        <div style={{ display: "flex", flexDirection: "column", gap: token.s4 }}>
          <FormField label={t("reception.field.groupeSanguin")}>
            {(p) => (
              <Input
                {...p}
                mono
                style={{ maxWidth: "12ch" }}
                value={clinique.groupe_sanguin}
                onChange={(e) => setClinique((s) => ({ ...s, groupe_sanguin: e.target.value }))}
              />
            )}
          </FormField>
          <FormField label={t("reception.field.antecedents")}>
            {(p) => (
              <Textarea
                {...p}
                value={clinique.antecedents}
                onChange={(e) => setClinique((s) => ({ ...s, antecedents: e.target.value }))}
              />
            )}
          </FormField>
          <FormField label={t("reception.field.traitements")}>
            {(p) => (
              <Textarea
                {...p}
                value={clinique.traitements}
                onChange={(e) => setClinique((s) => ({ ...s, traitements: e.target.value }))}
              />
            )}
          </FormField>
          <FormField label={t("reception.field.allergies")}>
            {(p) => (
              <Textarea
                {...p}
                value={clinique.allergies}
                onChange={(e) => setClinique((s) => ({ ...s, allergies: e.target.value }))}
              />
            )}
          </FormField>
        </div>
      </Card>

      <div style={{ display: "flex", gap: token.s3, justifyContent: "flex-end" }}>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
            {t("reception.form.cancel")}
          </Button>
        )}
        <Button type="submit" loading={saving} disabled={!valid}>
          {t("reception.form.save")}
        </Button>
      </div>
    </form>
  );
}
